import * as Sentry from '@sentry/react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { HTTPError } from 'ky';

import { ErrorFallback } from '../error-fallback';

interface RouterErrorBoundaryProps {
  error: Error;
  reset: () => void;
}

/**
 * Adapter component that bridges TanStack Router's errorComponent interface
 * with Sentry error reporting and our ErrorFallback UI.
 * This ensures route-level errors are properly captured and reported.
 */
export function RootRouterErrorBoundary({ error }: RouterErrorBoundaryProps) {
  const router = useRouter();
  const navigate = useNavigate();

  // Capture error to Sentry with route context
  Sentry.withScope((scope) => {
    // Mark this as a router-level error
    scope.setTag('errorBoundary', 'router-error');
    scope.setLevel('error');
    scope.setTag('errorType', 'route-error');

    // Add routing context
    scope.setContext('route', {
      path: router.state.location.pathname,
      search: router.state.location.search,
      routeId: router.state.matches[router.state.matches.length - 1]?.routeId,
    });

    // Add HTTP context if it's an HTTP error
    if (error instanceof HTTPError) {
      scope.setContext('http', {
        status: error.response.status,
        url: error.response.url,
      });
    }

    // Capture the error
    Sentry.captureException(error);
  });

  // Use the existing ErrorFallback component for consistent UI
  return (
    <ErrorFallback
      error={error}
      reset={() => {
        navigate({
          to: '/',
        });
      }}
      title="Unexpected Error"
      description="We encountered an unexpected error. Please try again or navigate to a different page. If the problem persists, please contact support."
    />
  );
}
